/**
 * Marked-times comparison page — the heatmap's sibling view (PLAN.md "Marked-time comparison &
 * AI narrowing"). Where the heatmap splits the day into sequential hours, THIS page splits the
 * strip into the user's OWN shortlist: every time they pressed "★ Add time to compare" on the
 * drill-down, laid side by side with the SAME features (metric toggle, value-gradient bars,
 * click → detail panel) plus:
 *
 *   • the full deterministic chart profile per time (D1/D9/D10/D60 lagnas, lagna lord, Moon
 *     nakshatra, mahadasha timeline, planet placements),
 *   • a facet-by-facet DIFFERENCES table (only what actually differs discriminates),
 *   • an AI narrowing chat that asks ONE lived-experience question per turn (with tappable answer
 *     options annotated by the times they support) and RANKS the shortlist itself — its
 *     promote/demote decisions are auto-applied to a running rank (owner spec 2026-07-05; the user
 *     no longer promotes/demotes/eliminates by hand).
 *
 * Honesty boundary, unchanged: the deterministic engine bars (events fit, accuracy, overall) are
 * the engine's own scores and stay separate; the assistant's running rank is a narrowing aid layered
 * beside them. The user's only decisions here are the FINAL pick (★) and removing a time from the list.
 */

import { useEffect, useRef, useState } from "react";
import { useProfile } from "../state/ProfileContext";
import { compare_chat_turn, type CompareOption, type RankSuggestion } from "../lib/compare_chat";
import {
  RANK_LIMIT,
  adjust_marked_rank,
  compare_conversation_stale,
  final_marked_key,
  has_compare_conversation,
  marked_rank,
  marked_time_key,
  normalize_marked_times,
  remove_marked_time,
  reset_compare_conversation,
  set_final_marked_key,
} from "../lib/time_compare";
import { audit_candidate, audit_sentence } from "../lib/candidate_audit";
import { carry_ai_cost, get_openai_api_key } from "../lib/openai_service";
import { Banner, Expander, LoadingLine, Spinner, useAsyncAction } from "../ui/common";
import { fullClock } from "../ui/format";
import {
  HeatmapBars,
  Legend,
  PanelSummary,
  SpecialPointsLine,
  fmtPct,
  type Bar,
  type MetricKey,
} from "./HeatmapScreen";

type ChatMsg = { role: "assistant" | "user"; text: string };

/** The three engine signals for an exact marked second, plus the assistant-driven running rank. */
const COMPARE_METRICS: Array<{ key: MetricKey; label: string; help: string }> = [
  {
    key: "events",
    label: "Events",
    help: "How well the dated events time out at this exact second (influence-weighted) — the engine's own signal.",
  },
  {
    key: "explains",
    label: "Explains",
    help: "How many of your dated events score above neutral at this exact second — the acceptance rule per candidate (same definition as the heatmap's Explains).",
  },
  {
    key: "accuracy",
    label: "Accuracy",
    help: "Held-out corroboration: dropping each event in turn, how stable this time's fit stays.",
  },
  {
    key: "derived",
    label: "Overall",
    help: "The engine's combined deterministic score (events primary; appearance bounded, never decisive).",
  },
  {
    key: "rank",
    label: "Rank",
    help: `The assistant's running rank (−${RANK_LIMIT}…+${RANK_LIMIT}), promoted/demoted automatically as you answer its questions. A narrowing aid — the engine scores above stay separate.`,
  },
];

/** Value label for the strip: engine metrics are %, the running rank is a signed level. */
function fmtMetricValue(v: number | null | undefined, metric: MetricKey): string {
  if (metric !== "rank") return v != null ? `${v.toFixed(1)}%` : "—";
  if (v == null || v === 0) return "0";
  return v > 0 ? `+${v.toFixed(0)}` : v.toFixed(0);
}
const fmtRank = (v: number) => fmtMetricValue(v, "rank");

const VERDICT_STYLE: Record<string, { color: string; label: string }> = {
  supported: { color: "var(--g2)", label: "supported" },
  contradicted: { color: "var(--danger-ink, #e07a7a)", label: "contradicted" },
  neutral: { color: "var(--ink3)", label: "neutral" },
};

/** "1961-07-01" + marked entries on ≥2 days → show the day beside the clock. */
function timeCaption(t: { time: string; date: string }, multiday: boolean): string {
  return multiday ? `${t.date} · ${fullClock(t.time)}` : fullClock(t.time);
}

export function CompareScreen({ onBack }: { onBack: () => void }) {
  const { user_profile, runOp, mutate } = useProfile();
  const [comparison, setComparison] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [metric, setMetric] = useState<MetricKey>("events");
  // A pending "I removed <time>" note for the assistant, delivered in-thread once the re-scored
  // comparison (without the removed time) has landed.
  const [removalNotice, setRemovalNotice] = useState<string | null>(null);
  // Bumped by the error banner's Retry — re-runs the comparison for the same signature.
  const [retryNonce, setRetryNonce] = useState(0);
  const loadedSig = useRef<string | null>(null);
  const generation = useRef(0);

  const entries = normalize_marked_times(user_profile ?? {});
  const multiday = new Set(entries.map((e) => e.date)).size > 1;
  // Re-score whenever the shortlist or the evidence changes.
  const sig = JSON.stringify({
    marked: entries.map(marked_time_key),
    events: user_profile?.events,
    traits: user_profile?.traits,
    rejected: user_profile?.rejected_traits,
    influence: user_profile?.trait_influence,
  });

  useEffect(() => {
    if (!entries.length) return;
    if (loadedSig.current === sig) return;
    loadedSig.current = sig;
    const token = ++generation.current;
    setComparison(null);
    setLoadError(null);
    runOp("compare_marked_times")
      .then((res) => {
        if (generation.current !== token) return;
        const result = res.result;
        if (!result?.available) {
          setLoadError(result?.reason ?? "Comparison unavailable.");
          // Clear the stamp so the Retry button (and the next render) can actually re-run —
          // without this the retry effect early-returns on the unchanged signature.
          loadedSig.current = null;
        } else {
          setComparison(result);
        }
      })
      .catch((e) => {
        // Only the LIVE run may report + clear the stamp — a superseded run must not wipe the
        // newer run's signature.
        if (generation.current === token) {
          setLoadError(String(e?.message ?? e));
          loadedSig.current = null;
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, entries.length, retryNonce]);

  // A NEW marked time invalidates the running conversation (owner spec 2026-07-05): every
  // question, assessment and running rank was built over the smaller shortlist, so reset the
  // whole conversation + AI scores; the chat then auto-reopens over the full set. (Pure removals
  // keep the thread — removeTime below tells the assistant in-thread instead.)
  const conversationStale = compare_conversation_stale(user_profile ?? {});
  useEffect(() => {
    if (!conversationStale) return;
    mutate((p: any) => {
      reset_compare_conversation(p);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationStale]);

  // With fewer than 2 marked times the chat panel early-returns ("mark at least two"), so a
  // queued "I removed <time>" note could never be delivered — drop it as moot.
  useEffect(() => {
    if (removalNotice && entries.length < 2) setRemovalNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removalNotice, entries.length]);

  if (!user_profile) return null;

  const assessments: Record<string, { verdict: string; reason?: string }> =
    user_profile.compare_assessments ?? {};
  const rankOf = (key: string) => marked_rank(user_profile, key);
  const finalKey = final_marked_key(user_profile);
  // The user's COMMITTED final birth time — their decision, saved to the `finals` record. Toggles.
  const setFinal = (key: string | null) =>
    mutate((p: any) => {
      set_final_marked_key(p, key);
    });
  const removeTime = (key: string) => {
    const entry = entries.find((e) => marked_time_key(e) === key);
    const label = entry ? timeCaption(entry, multiday) : key;
    // If a conversation is running, queue an in-thread note so the assistant knows the time is
    // out — delivered by the chat panel once the re-scored comparison lands.
    const hadConversation = has_compare_conversation(user_profile);
    mutate((p: any) => {
      remove_marked_time(p, key);
    });
    if (hadConversation) setRemovalNotice((prev) => (prev ? `${prev} and ${label}` : label));
    setSelectedKey(null);
  };

  const backBtn = (
    <button className="mono" onClick={onBack} style={{ cursor: "pointer" }}>
      ← Back to the heatmap
    </button>
  );

  if (!entries.length) {
    return (
      <div className="page2">
        <div className="heatmap-panel">
          <div className="heatmap-topbar">
            <div className="crumbs">{backBtn}</div>
          </div>
          <Banner kind="info">
            No times marked yet. Drill into a rising sign, click a candidate band, and press{" "}
            <strong>★ Add time to compare</strong> on times that feel likely — they collect here
            for a side-by-side verdict.
          </Banner>
        </div>
      </div>
    );
  }

  const times: any[] = comparison?.times ?? [];
  const byKey: Record<string, any> = Object.fromEntries(times.map((t) => [t.key, t]));
  const eventCount = comparison?.event_count ?? (user_profile.events ?? []).length;
  const selected = selectedKey ? (byKey[selectedKey] ?? null) : null;

  // Equal-width segments — the strip is the shortlist, not a time axis, so each marked time gets
  // the same visual room (chronological left→right; the caption carries the clock).
  const n = Math.max(times.length, 1);
  const bars: Bar[] = times.map((t, i) => {
    const rec = t.record ?? {};
    const det = rec.deterministic_score ?? rec.event_fit_score ?? null;
    const verdict = assessments[t.key]?.verdict;
    const rank = rankOf(t.key);
    return {
      key: t.key,
      leftFrac: i / n,
      widthFrac: 1 / n - 0.004,
      score: det != null ? Number(det) : null,
      name: fullClock(t.time),
      sub: t.sign ?? "",
      pct: det != null ? `${Number(det).toFixed(1)}%` : "—",
      mono: true,
      cursor: "pointer",
      hint: `${timeCaption(t, multiday)} · ${t.sign ?? "?"} rising${rank !== 0 ? ` · rank ${fmtRank(rank)}` : ""}${verdict ? ` · AI: ${verdict}` : ""}`,
      metrics: {
        events: rec.event_fit_score ?? null,
        explains: rec.explained_fit ?? null,
        accuracy: rec.accuracy_fit ?? null,
        derived: det != null ? Number(det) : null,
        rank,
        eras: null,
        appearance: null,
        personality: null,
      },
      onClick: () => setSelectedKey(t.key),
    };
  });

  const facets: any[] = comparison?.facets ?? [];
  const differingFacets = facets.filter((f) => f.differs);

  return (
    <div className="page2">
      <div className="heatmap-sticky">
        <div className="heatmap-panel">
          <div className="heatmap-topbar">
            <div className="crumbs">
              {backBtn}
              <span className="sep">›</span>
              <button className="here">Compare marked times · {entries.length}</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Legend rank />
            </div>
          </div>

          {finalKey && byKey[finalKey] && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                margin: "10px 0 2px",
                padding: "9px 14px",
                borderRadius: 10,
                border: "1px solid var(--accent)",
                background: "color-mix(in srgb, var(--accent) 12%, transparent)",
              }}
            >
              <span aria-hidden style={{ fontSize: 18 }}>
                ★
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <span style={{ fontSize: 12, color: "var(--ink3)", letterSpacing: 0.3, textTransform: "uppercase" }}>
                  Your final birth time
                </span>
                <strong style={{ fontFamily: "var(--mono)", fontSize: 16 }}>
                  {timeCaption(byKey[finalKey], multiday)}
                  <span style={{ fontWeight: 400, color: "var(--ink2)", marginLeft: 8, fontFamily: "inherit", fontSize: 13 }}>
                    {byKey[finalKey].sign ?? "?"} rising
                  </span>
                </strong>
              </div>
              <button
                type="button"
                onClick={() => setFinal(null)}
                className="caption"
                style={{
                  marginLeft: "auto",
                  border: "1px solid var(--line)",
                  background: "transparent",
                  color: "var(--ink3)",
                  borderRadius: 8,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
                title="Clear your final decision"
              >
                Clear
              </button>
            </div>
          )}

          <div className="level-caption">
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <h2>Your shortlist — which marked time is the birth?</h2>
              <span className="sub">{COMPARE_METRICS.find((m) => m.key === metric)?.help}</span>
            </div>
            <span className="res-note">exact seconds · D60 engaged</span>
          </div>

          <div className="metric-toggle" role="group" aria-label="Choose which metric the bars show">
            {COMPARE_METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`metric-toggle-btn${metric === m.key ? " active" : ""}`}
                aria-pressed={metric === m.key}
                title={m.help}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>

          {loadError && (
            <Banner kind="error">
              {loadError}{" "}
              <button className="mono" onClick={() => setRetryNonce((n) => n + 1)}>
                ↻ Retry
              </button>
            </Banner>
          )}
          {!loadError && !comparison && (
            <LoadingLine message="Casting the chart for each marked time (D1 → D60) and scoring your evidence…" />
          )}
          {comparison && times.length > 0 && (
            <HeatmapBars
              bars={bars}
              label={`Marked-times comparison — ${COMPARE_METRICS.find((m) => m.key === metric)?.label}`}
              metric={metric}
              rankColors
              formatValue={fmtMetricValue}
            />
          )}

          {/* AI verdict chips — the assistant's running read of each time (from your answers). */}
          {comparison && Object.keys(assessments).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              {times.map((t) => {
                const a = assessments[t.key];
                if (!a) return null;
                const v = VERDICT_STYLE[a.verdict] ?? VERDICT_STYLE.neutral;
                return (
                  <span key={t.key} className="top-fit-chip" title={a.reason ?? ""}>
                    <span className="top-fit-k">{timeCaption(t, multiday)}</span>
                    <span className="top-fit-v" style={{ color: v.color }}>
                      {v.label}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <MarkedTimeDetail
          item={selected}
          multiday={multiday}
          eventCount={eventCount}
          isFinal={selected ? finalKey === selected.key : false}
          assessment={selected ? (assessments[selected.key] ?? null) : null}
          rank={selected ? rankOf(selected.key) : 0}
          onSetFinal={selected ? () => setFinal(selected.key) : undefined}
          onRemove={selected ? () => removeTime(selected.key) : undefined}
          onClose={() => setSelectedKey(null)}
        />
      </div>

      {/* The AI narrowing chat — right under the heatmap unit; one question per turn. */}
      <section style={{ marginTop: 18 }}>
        <Expander
          summary={
            <PanelSummary
              no="✦"
              title="Narrow down with the assistant"
              meta="one question at a time — it ranks the shortlist for you"
            />
          }
          open
        >
          <CompareChatPanel
            comparison={comparison}
            multiday={multiday}
            finalKey={finalKey}
            onSetFinal={setFinal}
            removalNotice={removalNotice}
            onRemovalNoticeSent={() => setRemovalNotice(null)}
          />
        </Expander>
      </section>

      {/* Live ranking — the assistant maintains this from your answers (read-only). */}
      {comparison && times.length > 0 && (
        <section className="card" style={{ marginTop: 18 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontFamily: "var(--serif)", fontWeight: 500, fontSize: 18 }}>
              Live ranking
            </h3>
            <span className="caption">
              the assistant promotes ▲ / demotes ▼ each time automatically as you answer — the
              engine scores stay separate
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
            {[...times]
              .sort((a, b) => {
                const ra = rankOf(a.key);
                const rb = rankOf(b.key);
                if (rb !== ra) return rb - ra; // running rank first…
                const sa = a.record?.deterministic_score ?? -1;
                const sb = b.record?.deterministic_score ?? -1;
                return sb - sa; // …engine score breaks ties
              })
              .map((t, i) => {
                const rank = rankOf(t.key);
                const a = assessments[t.key];
                const v = a ? (VERDICT_STYLE[a.verdict] ?? VERDICT_STYLE.neutral) : null;
                return (
                  <div
                    key={t.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--line)",
                    }}
                  >
                    <span className="tree-score" style={{ fontSize: 11 }}>{`0${i + 1}`.slice(-2)}</span>
                    <strong style={{ fontFamily: "var(--mono)", fontSize: 13 }}>
                      {timeCaption(t, multiday)}
                    </strong>
                    <span className="caption">{t.sign ?? "?"} rising</span>
                    {finalKey === t.key && (
                      <span className="caption" style={{ color: "var(--accent)", fontWeight: 600 }} title="Your final birth time">
                        ★ final
                      </span>
                    )}
                    {v && (
                      <span className="caption" style={{ color: v.color }} title={a?.reason ?? ""}>
                        AI: {v.label}
                      </span>
                    )}
                    <span style={{ marginLeft: "auto" }} className="caption">
                      engine {fmtPct(t.record?.deterministic_score)}
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: 13,
                        fontWeight: 600,
                        minWidth: 32,
                        textAlign: "right",
                        color: rank > 0 ? "var(--g2)" : rank < 0 ? "var(--danger-ink)" : "var(--ink3)",
                      }}
                      title="The assistant's running rank for this time"
                    >
                      {fmtRank(rank)}
                    </span>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      {/* Differences table — collapsed by default; only rows that DIFFER can separate the times. */}
      {comparison && times.length > 1 && (
        <section style={{ marginTop: 18 }}>
          <Expander
            summary={
              <PanelSummary
                no="◆"
                title="What actually differs between these charts"
                meta={
                  differingFacets.length
                    ? `${differingFacets.length} of ${facets.length} chart facets separate your times`
                    : "no compared chart facet differs — the dated events are the only separator"
                }
              />
            }
          >
          <p className="caption" style={{ marginTop: 0 }}>
            {differingFacets.length
              ? `${differingFacets.length} of ${facets.length} chart facets separate your times — highlighted below. Facets the times share cannot decide between them.`
              : "These times are so close that no compared chart facet differs — the dated events (bars above) are the only separator."}
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="data" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  <th>Chart facet</th>
                  {times.map((t) => (
                    <th key={t.key} style={{ textAlign: "left", fontFamily: "var(--mono)" }}>
                      {timeCaption(t, multiday)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {facets.map((f) => (
                  <tr key={f.key} style={f.differs ? { background: "var(--bg3)" } : undefined}>
                    <td style={{ fontWeight: f.differs ? 600 : 400 }}>
                      {f.differs ? "◆ " : ""}
                      {f.label}
                    </td>
                    {times.map((t) => (
                      <td key={t.key} style={{ color: f.differs ? "var(--ink)" : "var(--ink3)", fontSize: 12.5 }}>
                        {f.values?.[t.key] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </Expander>
        </section>
      )}

      <p className="footer-note">
        The bars and engine scores are the deterministic Jyotish engine's; the assistant asks about
        what you lived and ranks the shortlist from your answers. The final call — the ★ — is yours.
      </p>
    </div>
  );
}

// ── The clicked-time detail panel (the comparison twin of CandidateDetailPanel) ───────────────

function MarkedTimeDetail({
  item,
  multiday,
  eventCount,
  isFinal,
  assessment,
  rank,
  onSetFinal,
  onRemove,
  onClose,
}: {
  item: any | null;
  multiday: boolean;
  eventCount: number;
  isFinal: boolean;
  assessment: { verdict: string; reason?: string } | null;
  rank: number;
  onSetFinal?: () => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  if (!item)
    return (
      <div className="heatmap-hover muted">
        <strong>Click</strong> a marked time to see its full chart profile, scores, and the set-final /
        remove controls.
      </div>
    );
  const rec = item.record ?? {};
  const facts = item.facts ?? null;
  const ul = { margin: "5px 0 0", paddingLeft: 16, listStyleType: "disc" } as const;
  const li = { marginBottom: 3, lineHeight: 1.45 } as const;
  const keyStyle = { color: "var(--ink3)" } as const;
  const btn = (label: string, onClick: (() => void) | undefined, danger = false, primary = false) =>
    onClick ? (
      <button
        type="button"
        onClick={onClick}
        style={{
          background: primary ? "var(--accent)" : "transparent",
          color: primary ? "#15151b" : danger ? "var(--danger-ink)" : "var(--ink2)",
          border: primary ? "none" : `1px solid ${danger ? "var(--danger-ink)" : "var(--line)"}`,
          borderRadius: 8,
          padding: "7px 12px",
          fontSize: 13,
          fontWeight: primary ? 600 : 400,
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    ) : null;

  return (
    <div className="heatmap-hover">
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ fontFamily: "var(--mono)", fontSize: 15 }}>{timeCaption(item, multiday)}</strong>
        <span style={{ color: "var(--ink2)" }}>{item.sign ?? "?"} rising</span>
        {rank !== 0 && (
          <span
            style={{ color: rank > 0 ? "var(--g2)" : "var(--danger-ink)", fontFamily: "var(--mono)", fontSize: 12 }}
            title="The assistant's running rank"
          >
            rank {fmtRank(rank)}
          </span>
        )}
        {isFinal && <span style={{ color: "var(--accent)", fontSize: 12, fontWeight: 600 }}>★ final</span>}
        <span className="muted">{audit_candidate(rec, eventCount).lead ?? ""}</span>
      </div>

      <ul style={ul}>
        <li style={li}>
          <span style={keyStyle}>Events fit:</span> <strong>{fmtPct(rec.event_fit_score)}</strong>
        </li>
        <li style={li}>
          <span style={keyStyle}>Explains:</span>{" "}
          <strong>
            {rec.explained_fit != null
              ? `${rec.explained_count ?? "?"}/${rec.events_total ?? "?"} events (${Number(rec.explained_fit).toFixed(1)}%)`
              : "—"}
          </strong>
        </li>
        <li style={li}>
          <span style={keyStyle}>Accuracy (held-out):</span> <strong>{fmtPct(rec.accuracy_fit)}</strong>
        </li>
        <li style={li}>
          <span style={keyStyle}>Overall (deterministic):</span> <strong>{fmtPct(rec.deterministic_score)}</strong>
        </li>
      </ul>
      <div className="caption" style={{ marginTop: 4 }}>
        {audit_sentence(rec, eventCount)}
      </div>
      <SpecialPointsLine sp={rec.special_points} />

      {facts && (
        <div style={{ marginTop: 8, borderTop: "1px solid var(--line)", paddingTop: 7 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              color: "var(--ink2)",
            }}
          >
            Chart profile
          </div>
          <ul style={ul}>
            <li style={li}>
              <span style={keyStyle}>D1 lagna:</span> {facts.d1_lagna} {facts.d1_lagna_degree}° · lord{" "}
              {facts.lagna_lord}
              {facts.lagna_lord_house ? ` in house ${facts.lagna_lord_house}` : ""}
            </li>
            <li style={li}>
              <span style={keyStyle}>Varga lagnas:</span> D9 {facts.d9_lagna} · D10 {facts.d10_lagna} · D60{" "}
              {facts.d60_lagna}
            </li>
            <li style={li}>
              <span style={keyStyle}>Moon:</span> {facts.moon_sign} · {facts.moon_nakshatra} pada {facts.moon_pada}
            </li>
            <li style={li}>
              <span style={keyStyle}>Dashas:</span>{" "}
              {(facts.dasha_timeline ?? [])
                .slice(0, 5)
                .map((p: any) => `${p.lord} ${p.start_year}–${p.end_year}`)
                .join(" · ")}
            </li>
          </ul>
        </div>
      )}

      {assessment && (
        <div className="caption" style={{ marginTop: 6 }}>
          <span style={{ color: (VERDICT_STYLE[assessment.verdict] ?? VERDICT_STYLE.neutral).color }}>
            AI read: {assessment.verdict}
          </span>
          {assessment.reason ? ` — ${assessment.reason}` : ""}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 10,
          borderTop: "1px solid var(--line)",
          paddingTop: 9,
          flexWrap: "wrap",
        }}
      >
        {onSetFinal && btn(isFinal ? "★ Final — tap to unset" : "★ Set as final birth time", onSetFinal, false, !isFinal)}
        {btn("🗑 Remove from comparison", onRemove, true)}
        {btn("Close", onClose)}
      </div>
    </div>
  );
}

// ── The AI narrowing chat (one question per turn; the assistant ranks the shortlist itself) ──────

function CompareChatPanel({
  comparison,
  multiday,
  finalKey,
  onSetFinal,
  removalNotice,
  onRemovalNoticeSent,
}: {
  comparison: any | null;
  multiday: boolean;
  finalKey: string | null;
  onSetFinal: (key: string | null) => void;
  removalNotice: string | null;
  onRemovalNoticeSent: () => void;
}) {
  const { user_profile, run_data, mutate, reload } = useProfile();
  const chatAction = useAsyncAction();
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState<{ user: string; reply: string } | null>(null);
  // The latest turn's structured output — session-local, regenerated each turn. `options` are the
  // tappable answers to the current question; `moves` is the rank change the assistant just APPLIED
  // (shown read-only); `mostLikely` is its advisory lead. The durable narrative lives in
  // profile.compare_chat and the running rank in profile.compare_ranks.
  const [options, setOptions] = useState<CompareOption[]>([]);
  const [moves, setMoves] = useState<RankSuggestion[]>([]);
  const [mostLikely, setMostLikely] = useState<string | null>(null);
  const logEndRef = useRef<HTMLDivElement>(null);
  const hasKey = !!get_openai_api_key();
  // Auto-start guards: `runTurnRef` gives the effect the latest turn fn; `autoStartRef` ensures the
  // opening turn fires at most once (StrictMode-safe).
  const runTurnRef = useRef<(m: string) => void>(() => {});
  const autoStartRef = useRef(false);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ block: "end" });
  }, [streaming, user_profile]);

  // Open the narrowing chat automatically: as soon as the comparison is ready and there are ≥2
  // times to weigh, the assistant asks its first question with no click — so the page lands
  // mid-conversation instead of on an empty "press Start" box.
  const canAutoStart =
    hasKey &&
    !!comparison?.available &&
    (comparison?.times ?? []).length >= 2 &&
    (user_profile?.compare_chat ?? []).length === 0 &&
    !user_profile?.compare_conversation_id;
  useEffect(() => {
    if (!canAutoStart || autoStartRef.current) return;
    autoStartRef.current = true;
    void runTurnRef.current("");
  }, [canAutoStart]);

  // A queued "I removed <time>" note is NOT auto-sent (owner spec 2026-07-05): the chat input is
  // disabled and an explicit "Update" button delivers it — so several quick removals batch into
  // ONE update and there is never a race of auto-fired messages against the API. This effect only
  // drops a notice that became MOOT (the conversation is gone, e.g. cleared/reset meanwhile).
  const stillConversing =
    Boolean(user_profile?.compare_conversation_id) || ((user_profile?.compare_chat ?? []).length > 0);
  useEffect(() => {
    if (removalNotice && !stillConversing) onRemovalNoticeSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removalNotice, stillConversing]);

  // The Update button stays disabled until the RE-SCORED comparison (without the removed time)
  // has landed — right after a removal the old comparison is briefly still in state, and sending
  // then would hand the assistant a context (and schema key-enum) that still includes the
  // removed time.
  const comparisonFresh = (() => {
    if (!comparison?.available) return false;
    const comparisonKeys = ((comparison.times ?? []) as any[]).map((t: any) => String(t.key));
    const markedKeys = normalize_marked_times(user_profile ?? {}).map(marked_time_key);
    return comparisonKeys.length === markedKeys.length && comparisonKeys.every((k, i) => k === markedKeys[i]);
  })();

  if (!user_profile) return null;
  const chat: ChatMsg[] = (user_profile.compare_chat ?? []) as ChatMsg[];
  const byKey: Record<string, any> = Object.fromEntries(
    ((comparison?.times ?? []) as any[]).map((t) => [t.key, t]),
  );
  const label = (key: string) => {
    const t = byKey[key];
    return t ? timeCaption(t, multiday) : key;
  };

  if (!hasKey) {
    return (
      <Banner kind="warn">
        No OpenAI key is configured, so the narrowing chat is unavailable — the comparison table
        above still shows every deterministic difference.
      </Banner>
    );
  }
  if (!comparison?.available) {
    return <p className="muted">The chat opens once the comparison has finished computing.</p>;
  }
  if ((comparison.times ?? []).length < 2) {
    return (
      <p className="muted">
        Mark at least two times — with a single candidate there is nothing to narrow.
      </p>
    );
  }

  async function runTurn(message: string) {
    const text = message.trim();
    setDraft("");
    setOptions([]);
    setMoves([]);
    setMostLikely(null); // clear last turn's read so an errored turn can't leave a stale banner
    setStreaming({ user: text, reply: "" });
    await chatAction.run("Reading the chart differences…", async () => {
      const run = structuredClone(run_data ?? {});
      const result = await compare_chat_turn(
        (user_profile!.compare_conversation_id as string | null) ?? null,
        text,
        comparison,
        [],
        user_profile!.compare_assessments ?? {},
        user_profile!.compare_ranks ?? {},
        run,
        user_profile!.slug ?? null,
        (partial) => setStreaming((s) => ({ user: s?.user ?? text, reply: partial })),
      );
      if (result.error) {
        // The turn may have created/advanced the server-side conversation before failing on OUR
        // side — keep the id so the next send continues the same thread, and restore the user's
        // text so nothing typed is lost. A failed turn can still have been BILLED, so its spend
        // is carried across either way.
        if (result.conversation_id && result.conversation_id !== user_profile!.compare_conversation_id) {
          mutate((p: any, r: any) => {
            carry_ai_cost(r, run);
            p.compare_conversation_id = result.conversation_id;
            // Stamp the keys this (failed) turn was opened over. Without this, a FIRST-turn
            // failure leaves a conversation id with no key bookkeeping, which the stale-reset
            // effect reads as legacy-stale and immediately wipes — defeating the id we just
            // preserved for the retry.
            const live = new Set(normalize_marked_times(p).map(marked_time_key));
            p.compare_chat_keys = ((comparison?.times ?? []) as any[])
              .map((t: any) => String(t.key))
              .filter((k: string) => live.has(k));
          });
        } else {
          mutate((_p: any, r: any) => carry_ai_cost(r, run));
        }
        setDraft(text);
        throw new Error(String(result.error));
      }
      setOptions(result.options ?? []);
      // Only promote/demote decisions actually move a rank — surface those as the "just applied" note.
      setMoves((result.rank_suggestions ?? []).filter((s: RankSuggestion) => s.action !== "keep"));
      setMostLikely(result.most_likely ?? null);
      mutate((p: any, r: any) => {
        const log: ChatMsg[] = Array.isArray(p.compare_chat) ? [...p.compare_chat] : [];
        if (text) log.push({ role: "user", text });
        if (result.reply) log.push({ role: "assistant", text: result.reply });
        p.compare_chat = log;
        p.compare_conversation_id = result.conversation_id ?? p.compare_conversation_id;
        // Keys still on the shortlist at write time — a turn raced by a concurrent removal must
        // not resurrect the removed time (in the stamp, an assessment, or a rank).
        const onList = new Set(normalize_marked_times(p).map(marked_time_key));
        // Stamp the shortlist this conversation has now seen — a key missing from this list
        // later means a NEW time joined, which resets the conversation (compare_conversation_stale).
        p.compare_chat_keys = ((comparison?.times ?? []) as any[])
          .map((t: any) => String(t.key))
          .filter((k: string) => onList.has(k));
        // Merge the fresh assessments over the old — a time the model didn't re-assess keeps its
        // last verdict.
        for (const [k, a] of Object.entries(result.assessments ?? {})) {
          if (!onList.has(k)) continue;
          p.compare_assessments = { ...(p.compare_assessments ?? {}), [k]: a };
        }
        // AUTO-APPLY the assistant's rank decisions (owner spec 2026-07-05): the system ranks
        // itself from the user's answer — one step per promote/demote, clamped.
        for (const s of result.rank_suggestions ?? []) {
          if (!onList.has(s.time)) continue;
          if (s.action === "promote") adjust_marked_rank(p, s.time, 1);
          else if (s.action === "demote") adjust_marked_rank(p, s.time, -1);
        }
        r.log = run.log ?? r.log;
        carry_ai_cost(r, run);
      });
      reload();
    });
    setStreaming(null);
  }
  runTurnRef.current = runTurn;

  // Deliver the batched removal update — the USER's click, one message no matter how many
  // removals were queued.
  const sendUpdate = () => {
    const notice = removalNotice;
    if (!notice || chatAction.pending || !comparisonFresh) return;
    onRemovalNoticeSent();
    void runTurn(
      `Quick update: I've removed ${notice} from my shortlist — take it out of consideration and continue with the remaining times.`,
    );
  };

  // Start over: wipe the conversation + AI scores and open a FRESH thread over the currently
  // selected times (the auto-start effect re-fires once re-armed).
  const clearConversation = () => {
    if (chatAction.pending) return;
    onRemovalNoticeSent(); // any queued removal note is moot for a fresh thread
    setOptions([]);
    setMoves([]);
    setMostLikely(null);
    autoStartRef.current = false;
    mutate((p: any) => {
      reset_compare_conversation(p);
    });
  };

  return (
    <div>
      {chat.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
          <button
            type="button"
            onClick={clearConversation}
            disabled={chatAction.pending}
            title="Clear this conversation and start fresh with the currently selected times"
            style={{
              border: "1px solid var(--line)",
              background: "transparent",
              color: "var(--ink3)",
              borderRadius: 8,
              padding: "4px 10px",
              fontSize: 12,
              cursor: chatAction.pending ? "default" : "pointer",
            }}
          >
            ↺ Clear conversation
          </button>
        </div>
      )}

      <div className="trait-chat-log">
        {chat.length === 0 && !streaming && !chatAction.pending && !chatAction.error && (
          <p className="muted">
            Opening the conversation — the assistant is reading your charts and will ask its first
            question in a moment…
          </p>
        )}
        {chat.map((m, i) => (
          <div key={i} className={`chat-msg ${m.role}`}>
            <span>{m.text}</span>
          </div>
        ))}
        {chatAction.pending && streaming && (
          <>
            {streaming.user && (
              <div className="chat-msg user">
                <span>{streaming.user}</span>
              </div>
            )}
            <div className="chat-msg assistant">
              {streaming.reply ? (
                <span>
                  {streaming.reply}
                  <span className="chat-caret" />
                </span>
              ) : (
                <span className="chat-typing" aria-label="Assistant is typing">
                  <span />
                  <span />
                  <span />
                </span>
              )}
            </div>
          </>
        )}
        <div ref={logEndRef} />
      </div>

      {/* This turn's tappable answer options, each annotated with the times it supports.
          Blocked while a removal Update is pending — answering then would race the shortlist. */}
      {!chatAction.pending && options.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "10px 0" }}>
          {options.map((o, oi) => (
            <button
              key={oi}
              type="button"
              disabled={!!removalNotice}
              onClick={() => runTurn(o.label)}
              title={o.supports.length ? `Points toward: ${o.supports.map(label).join(", ")}` : "Doesn't point at a specific time"}
              style={{
                border: "1px solid var(--line)",
                background: "var(--bg3)",
                color: "var(--ink)",
                borderRadius: 999,
                padding: "6px 13px",
                fontSize: 13,
                cursor: removalNotice ? "default" : "pointer",
                opacity: removalNotice ? 0.5 : 1,
              }}
            >
              {o.label}
              {o.supports.length > 0 && (
                <span style={{ color: "var(--ink3)", marginLeft: 6, fontSize: 11.5 }}>
                  ({o.supports.map(label).join(", ")})
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* What the assistant just did to the ranking (read-only — it applies these itself). */}
      {!chatAction.pending && moves.length > 0 && (
        <div className="caption" style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", margin: "6px 0" }}>
          <span>From your answer, the assistant adjusted:</span>
          {moves.map((s, i) => {
            const up = s.action === "promote";
            return (
              <span
                key={`${s.time}:${i}`}
                title={s.reason}
                style={{
                  border: `1px solid ${up ? "var(--g2)" : "var(--danger-ink)"}`,
                  color: up ? "var(--g2)" : "var(--danger-ink)",
                  borderRadius: 999,
                  padding: "2px 9px",
                  fontSize: 12,
                }}
              >
                {up ? "▲" : "▼"} {label(s.time)}
              </span>
            );
          })}
        </div>
      )}

      {/* The advisory "most likely" read from the latest turn — one tap to make it your final. */}
      {!chatAction.pending && mostLikely && byKey[mostLikely] && (
        <div
          className="caption"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            margin: "8px 0",
            padding: "7px 10px",
            borderRadius: 8,
            border: "1px solid var(--accent)",
            color: "var(--accent)",
          }}
        >
          <span>
            ☄ The combined evidence currently favours <strong>{label(mostLikely)}</strong> — advisory
            only; you make the final call.
          </span>
          {finalKey !== mostLikely && (
            <button
              type="button"
              onClick={() => onSetFinal(mostLikely)}
              style={{
                border: "1px solid var(--accent)",
                background: "var(--accent)",
                color: "#15151b",
                borderRadius: 999,
                padding: "3px 10px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
              title="Record this as your final birth time"
            >
              ★ Set as final
            </button>
          )}
        </div>
      )}

      {chatAction.error && <Banner kind="error">{chatAction.error}</Banner>}

      {/* A pending removal blocks the input behind an explicit Update — several quick removals
          batch into ONE message, so there is never a race of auto-sent updates. */}
      {removalNotice && !chatAction.pending && (
        <div
          className="row"
          style={{
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid var(--line)",
            background: "var(--bg3)",
            marginBottom: 8,
          }}
        >
          <span className="caption" style={{ flex: 1 }}>
            Shortlist changed — you removed <strong>{removalNotice}</strong>. Press{" "}
            <strong>Update</strong> to tell the assistant and continue.
          </span>
          <button
            className="primary"
            type="button"
            disabled={!comparisonFresh}
            title={comparisonFresh ? "Tell the assistant what changed" : "Re-scoring the shortlist…"}
            onClick={sendUpdate}
            style={{ borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600 }}
          >
            {comparisonFresh ? "Update" : <Spinner />}
          </button>
        </div>
      )}

      <div className="row" style={{ alignItems: "flex-end" }}>
        <textarea
          rows={2}
          style={{ flex: 1 }}
          value={draft}
          disabled={chatAction.pending || !!removalNotice}
          placeholder={
            removalNotice
              ? "Press Update above to tell the assistant about the removed time…"
              : chat.length === 0
                ? "The assistant opens the conversation automatically — or type an answer of your own…"
                : "Tap an option above, or answer in your own words…  (Enter to send · Ctrl+Enter for a new line)"
          }
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            if (e.shiftKey) return;
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              const el = e.currentTarget;
              const start = el.selectionStart ?? draft.length;
              const end = el.selectionEnd ?? draft.length;
              const next = draft.slice(0, start) + "\n" + draft.slice(end);
              setDraft(next);
              requestAnimationFrame(() => {
                el.selectionStart = el.selectionEnd = start + 1;
              });
              return;
            }
            e.preventDefault();
            if (chatAction.pending || !!removalNotice || (chat.length > 0 && !draft.trim())) return;
            void runTurn(draft);
          }}
        />
        <button
          className="primary trait-chat-send"
          aria-label={chat.length === 0 && !draft.trim() ? "Start" : "Send"}
          title={chat.length === 0 && !draft.trim() ? "Start" : "Send"}
          disabled={chatAction.pending || !!removalNotice || (chat.length > 0 && !draft.trim())}
          onClick={() => void runTurn(draft)}
        >
          {chatAction.pending ? <Spinner /> : <span aria-hidden>▶</span>}
        </button>
      </div>
    </div>
  );
}
